"use client"

import { useAboutUs } from "@/hook/useAboutUs"
import { toImageSrc } from "@/lib/utils"
import { AboutUsTimelineProps } from "@/types/types"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import { twMerge } from "tailwind-merge"
import { generateResponsiveCSS } from "../../../lib/helper"
import { TimelineContent } from "./components/TimelineContent"
import { TimelineNavigation } from "./components/TimelineNavigation"

export const RenderConfig: ComponentConfig<AboutUsTimelineProps> = {
  render: ({ title, items, logo, styles, responsiveType, className }) => {
    const id = `about-us-timeline-${Math.random().toString(36).substr(2, 9)}`
    const mergedStyles = styles || {}
    const responsiveCSS = generateResponsiveCSS(id, mergedStyles, responsiveType || "mobileFirst")

    const { currentIndex, years, goToSlide, goToNext, goToPrev, goToYear, canGoPrev, canGoNext, setCurrentIndex } =
      useAboutUs(items)

    const handleSlideChange = (index: number) => {
      setCurrentIndex(index)
    }

    const handleYearClick = (index: number) => {
      goToSlide(index)
    }

    if (!items || items.length === 0) {
      return (
        <>
          {responsiveCSS}
          <div id={id} className={twMerge(id, className)}>
            <div className="text-center py-20 text-gray-500">No timeline items available.</div>
          </div>
        </>
      )
    }

    const currentItem = items[currentIndex]
    const currentImageUrl = currentItem?.image ? toImageSrc(currentItem.image) : null

    return (
      <>
        {responsiveCSS}
        <div
          id={id}
          className={twMerge(
            "container mx-auto max-w-[1260px] pt-8 px-4 sm:pt-[6.25rem] sm:pb-8 sm:px-0",
            id,
            className,
          )}
        >
          {/* Main Content Area - Image Left, All Text Right */}
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-[1.875rem] sm:w-[1260px]">
            {/* Left Side - Image Only - Hidden on mobile */}
            {currentImageUrl && (
              <div className="hidden sm:flex w-full sm:w-auto flex-shrink-0 justify-center sm:justify-start">
                <div className="relative w-full max-w-[507.5px] h-[300px] sm:w-[507.5px] sm:h-[507px] rounded-lg overflow-hidden">
                  <Image
                    src={currentImageUrl}
                    alt={currentItem?.title || "Timeline Image"}
                    fill
                    className="object-cover"
                  />
                </div>
              </div>
            )}

            {/* Right Side - All Text Content */}
            <div className="flex-1 flex flex-col sm:w-[722.5px]">
              {/* Title "LỊCH SỬ" */}
              <h2 className="text-2xl font-black leading-[1.625rem] mb-[3rem] sm:mb-12 text-[#333333] uppercase text-center sm:text-left">
                {title}
              </h2>

              {/* Timeline Navigation - Carousel timeline ngay dưới "Lịch sử" */}
              <div className="mb-4 sm:mb-0">
                <TimelineNavigation
                  years={years}
                  activeIndex={currentIndex}
                  onYearClick={handleYearClick}
                  onPrevClick={goToPrev}
                  onNextClick={goToNext}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                />
              </div>

              {/* Timeline Content - Text Only (Title, Subtitle, Description, tamdaWing) */}
              <div className="flex-1">
                <TimelineContent
                  items={items}
                  currentIndex={currentIndex}
                  onSlideChange={handleSlideChange}
                  showImageOnly={false}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    )
  },
}
